import { useCallback } from "react"
import { useTranslation } from "react-i18next"

import {
  DateFilterDto,
  DateFilterOperator,
  SlidingPreset,
} from "@tfg/types/types/date-filter.dto"

import { formatDate } from "~/utils/date"

export const useFiltersOperatorsLabelsTranslations = () => {
  const { t } = useTranslation(["common"])

  return {
    [DateFilterOperator.BETWEEN]: t("table.dateFilter.operator.between"),
    [DateFilterOperator.EQUALS]: t("table.dateFilter.operator.equals"),
    [DateFilterOperator.BEFORE]: t("table.dateFilter.operator.before"),
    [DateFilterOperator.AFTER]: t("table.dateFilter.operator.after"),
    [DateFilterOperator.IS_EMPTY]: t("table.dateFilter.operator.empty"),
    [DateFilterOperator.IS_NOT_EMPTY]: t("table.dateFilter.operator.notEmpty"),
    [DateFilterOperator.NOT_EQUALS]: t("table.dateFilter.operator.notEquals"),
    [DateFilterOperator.IS_BEFORE_OR_EQUALS]: t(
      "table.dateFilter.operator.beforeOrEquals"
    ),
    [DateFilterOperator.IS_AFTER_OR_EQUALS]: t(
      "table.dateFilter.operator.afterOrEquals"
    ),
    [DateFilterOperator.IS_NOT_BETWEEN]: t(
      "table.dateFilter.operator.notBetween"
    ),
  }
}

export type FiltersOperatorsValuesResolver = (filter: DateFilterDto) => string

export const useFiltersOperatorsValuesResolver = (
  slidingPresetLabelTranslationMapping: Record<SlidingPreset, string>
): FiltersOperatorsValuesResolver => {
  const { t } = useTranslation(["common"])

  const labelResolver = useCallback(
    (filter: DateFilterDto): string => {
      const { range, operator, preset } = filter

      const isDateRangeOperator =
        operator !== DateFilterOperator.IS_EMPTY &&
        operator !== DateFilterOperator.IS_NOT_EMPTY

      if (preset !== SlidingPreset.CUSTOM && isDateRangeOperator) {
        return slidingPresetLabelTranslationMapping[preset] ?? ""
      }

      const formattedStartDate = range?.startDate
        ? formatDate(range?.startDate)
        : ""
      const formattedEndDate = range?.endDate ? formatDate(range?.endDate) : ""

      const formattedDates =
        formattedStartDate && formattedEndDate
          ? `${formattedStartDate} - ${formattedEndDate}`
          : formattedStartDate || formattedEndDate

      const labelResolver = {
        [DateFilterOperator.BETWEEN]: `${t("table.dateFilter.operator.between")} ${formattedDates}`,
        [DateFilterOperator.EQUALS]: `${t("table.dateFilter.operator.equals")} ${formattedStartDate}`,
        [DateFilterOperator.BEFORE]: `${t("table.dateFilter.operator.before")} ${formattedStartDate}`,
        [DateFilterOperator.AFTER]: `${t("table.dateFilter.operator.after")} ${formattedStartDate}`,
        [DateFilterOperator.NOT_EQUALS]: `${t("table.dateFilter.operator.notEquals")} ${formattedStartDate}`,
        [DateFilterOperator.IS_BEFORE_OR_EQUALS]: `${t("table.dateFilter.operator.beforeOrEquals")} ${formattedStartDate}`,
        [DateFilterOperator.IS_AFTER_OR_EQUALS]: `${t("table.dateFilter.operator.afterOrEquals")} ${formattedStartDate}`,
        [DateFilterOperator.IS_NOT_BETWEEN]: `${t("table.dateFilter.operator.notBetween")} ${formattedDates}`,
        [DateFilterOperator.IS_NOT_EMPTY]: t(
          "table.dateFilter.operator.notEmpty"
        ),
        [DateFilterOperator.IS_EMPTY]: t("table.dateFilter.operator.empty"),
      }

      return labelResolver[operator]
    },
    [t, slidingPresetLabelTranslationMapping]
  )

  return labelResolver
}

export const useFiltersSlidingPresetsLabelsTranslations = () => {
  const { t } = useTranslation(["common"])

  return {
    [SlidingPreset.TODAY]: t("table.dateFilter.preset.today"),
    [SlidingPreset.YESTERDAY]: t("table.dateFilter.preset.yesterday"),
    [SlidingPreset.TOMORROW]: t("table.dateFilter.preset.tomorrow"),
    [SlidingPreset.ONE_WEEK_AGO]: t("table.dateFilter.preset.oneWeekAgo"),
    [SlidingPreset.ONE_WEEK_FROM_NOW]: t(
      "table.dateFilter.preset.oneWeekFromNow"
    ),
    [SlidingPreset.ONE_MONTH_AGO]: t("table.dateFilter.preset.oneMonthAgo"),
    [SlidingPreset.ONE_MONTH_FROM_NOW]: t(
      "table.dateFilter.preset.oneMonthFromNow"
    ),
    [SlidingPreset.ONE_YEAR_AGO]: t("table.dateFilter.preset.oneYearAgo"),
    [SlidingPreset.ONE_YEAR_FROM_NOW]: t(
      "table.dateFilter.preset.oneYearFromNow"
    ),
    [SlidingPreset.CUSTOM]: t("table.dateFilter.preset.custom"),
  }
}
